import { useState, useEffect } from 'react'
import styles from './CreateTaskModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function CreateTaskModal({ 
    isOpen, 
    onClose, 
    onSave,
    project = null,
    task = null, // Task to edit (null for create mode)
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        status: 'active',
        color: '#3B82F6',
        is_billable: true
    })
    
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})

    // Edit mode is only when we have a task with an ID
    const isEditMode = !!(task && task.id)

    // Handle escape key to close modal (disabled when loading)
    useEscapeKey(isOpen, onClose, !loading)

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && task && task.id) {
                // Pre-fill form with existing task data for editing
                setFormData({
                    name: task.name || '',
                    status: task.status || 'active',
                    color: task.color || '#3B82F6',
                    is_billable: task.is_billable !== undefined ? task.is_billable : true
                })
            } else if (task && task.is_billable !== undefined) {
                // Create mode with pre-defined billable status from parent
                setFormData({
                    name: '',
                    status: 'active',
                    color: '#3B82F6',
                    is_billable: task.is_billable
                })
            } else {
                // Reset form for create mode (default billable)
                setFormData({
                    name: '',
                    status: 'active',
                    color: '#3B82F6',
                    is_billable: true
                })
            }
            setErrors({})
        }
    }, [isOpen, isEditMode, task])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.name.trim()) {
            newErrors.name = 'Task name is required'
        }
        
        if (formData.name.trim().length > 200) {
            newErrors.name = 'Task name must be less than 200 characters'
        }
        
        // Validate color format (hex color)
        if (formData.color && !/^#[0-9A-F]{6}$/i.test(formData.color)) {
            newErrors.color = 'Please select a valid color'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        if (!project || !project.id) {
            setErrors({ general: `Project is required to ${isEditMode ? 'update' : 'create'} a task` })
            return
        }
        
        setLoading(true)
        
        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }
            
            apiClient.setAuthToken(apiToken)
            
            const taskData = {
                name: formData.name.trim(),
                status: formData.status,
                color: formData.color,
                is_billable: formData.is_billable
            }
            
            const response = isEditMode 
                ? await apiClient.updateTask(project.id, task.id, taskData)
                : await apiClient.createTask(project.id, taskData)
            
            // Notify parent component of successful creation/update
            if (onSave) {
                onSave(response)
            }
            
            onClose()
        } catch (error) {
            console.error(`Failed to ${isEditMode ? 'update' : 'create'} task:`, error)
            
            // Handle validation errors from backend
            if (error.message.includes('details')) {
                setErrors({ general: error.message })
            } else {
                setErrors({ 
                    general: error.message || `Failed to ${isEditMode ? 'update' : 'create'} task. Please try again.` 
                })
            }
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    // Predefined color options
    const colorOptions = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#F97316', // Orange
        '#06B6D4', // Cyan
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
    ]

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{isEditMode ? 'Edit Task' : 'Create New Task'}</h2>
                    <p className={styles.projectName}>for {project?.name}</p>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* General Error Display */}
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    {/* Task Name */}
                    <div className={styles.formGroup}>
                        <label htmlFor="taskName" className={styles.label}>
                            Task Name *
                        </label>
                        <input
                            type="text"
                            id="taskName"
                            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter task name"
                            maxLength="200"
                            autoFocus
                            disabled={loading}
                        />
                        {errors.name && (
                            <span className={styles.fieldError}>{errors.name}</span>
                        )}
                    </div>

                    <div className={styles.inputRow}>
                        {/* Status Selection */}
                        <div className={styles.formGroup}>
                            <label htmlFor="status" className={styles.label}>
                                Status
                            </label>
                            <select
                                id="status"
                                className={styles.select}
                                value={formData.status}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                disabled={loading}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        {/* Color Selection */}
                        <div className={styles.formGroup}>
                            <label htmlFor="color" className={styles.label}>
                                Task Color *
                            </label>
                            <div className={styles.colorSection}>
                                <input
                                    type="color"
                                    id="color"
                                    className={`${styles.colorInput} ${errors.color ? styles.inputError : ''}`}
                                    value={formData.color}
                                    onChange={(e) => handleInputChange('color', e.target.value)}
                                    disabled={loading}
                                />
                                <div className={styles.colorOptions}>
                                    {colorOptions.map(color => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`${styles.colorOption} ${formData.color === color ? styles.colorOptionSelected : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleInputChange('color', color)}
                                            disabled={loading}
                                            aria-label={`Select color ${color}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            {errors.color && (
                                <span className={styles.fieldError}>{errors.color}</span>
                            )}
                        </div>
                    </div>

                    {/* Billable Toggle */}
                    <div className={styles.toggleSetting}>
                        <div className={styles.toggleInfo}>
                            <label>Billable</label>
                            <p>Mark this task as billable by default</p>
                        </div>
                        <input
                            type="checkbox"
                            className={styles.toggle}
                            checked={formData.is_billable}
                            onChange={(e) => handleInputChange('is_billable', e.target.checked)}
                            disabled={loading}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Task' : 'Create Task')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}